import React from 'react';
// @ts-expect-error - No type declarations available for this component
import StyledDropdown from '../../dropdowns/StyledDropdown';
import MultiSelectDropdownNew from '../../dropdowns/MultiSelectDropdownNew';
import { Draggable } from '@hello-pangea/dnd';
import { Label } from '../../../components/ui/label';
import { FormInput } from '../../../components/ui/form-input';
import { Switch } from '../../../../components/ui/switch';
import { InputType } from './CustomInputsTab';

const INPUT_TYPE_MAPPING = {
  SHORT_TEXT_INPUT: 'Short Text Input',
  LONG_TEXT_INPUT: 'Long Text Input',
  SINGLE_SELECTION_DROPDOWN: 'Single Selection Dropdown',
  MULTI_SELECTION_DROPDOWN: 'Multi Selection Dropdown',
};

interface CustomInput {
  id: string;
  title: string;
  type: InputType | string;
  options: string[];
  required: boolean;
}

interface CustomInputsTabContentProps {
  customInput: CustomInput;
  id: string;
  index: number;
  handleTitleChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  handleTypeChange: (event: React.MouseEvent<HTMLAnchorElement>) => void;
  handleOptionsChange: (options: string[], index: number) => void;
  handleRequiredChange: (value: boolean, index: number) => void;
  removeCustomInput: (index: number) => void;
  originalCustomInputs?: CustomInput[];
  isFieldInvalid?: (fieldName: string) => boolean;
  handleFieldFocus?: (fieldName: string) => void;
}

function CustomInputsTabContent(props: CustomInputsTabContentProps) {
  const {
    customInput,
    id,
    index,
    handleTitleChange,
    handleTypeChange,
    handleOptionsChange,
    handleRequiredChange,
    removeCustomInput,
    isFieldInvalid,
    handleFieldFocus,
  } = props;

  return (
    <Draggable draggableId={id} index={index}>
      {(provided) => (
        <div
          {...provided.draggableProps}
          ref={provided.innerRef}
          className="space-y-2 rounded-md border border-edge bg-paper-inset p-2"
        >
          {/* Question Field with drag/delete icons */}
          <div className="space-y-1">
            <div className="flex items-start justify-between">
              <Label className="text-base font-medium text-ink-secondary">Question</Label>
              <div className="flex items-center gap-1">
                <div {...provided.dragHandleProps} className="cursor-move">
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                    <path
                      d="M2 4H14M2 8H14M2 12H14"
                      stroke="#3A3429"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                    />
                  </svg>
                </div>
                <button onClick={() => removeCustomInput(index)} className="hover:opacity-70">
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                    <path
                      d="M4 4L12 12M4 12L12 4"
                      stroke="#3A3429"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                    />
                  </svg>
                </button>
              </div>
            </div>
            <FormInput
              id={`question-${index}`}
              data-type="title"
              data-index={index}
              value={customInput.title}
              onChange={handleTitleChange}
              onFocus={() => handleFieldFocus?.(`custom_fields-customInput-title-${index}`)}
              className={`h-7 ${
                isFieldInvalid?.(`custom_fields-customInput-title-${index}`)
                  ? 'border-red-500 focus:border-red-500 focus:ring-red-500'
                  : ''
              }`}
            />
          </div>

          {/* Input Type Field */}
          <div className="space-y-1">
            <Label className="text-base font-medium text-ink-secondary">Input Type</Label>
            <div
              className={`${
                isFieldInvalid?.(`custom_fields-customInput-type-${index}`)
                  ? 'rounded-md ring-1 ring-red-500'
                  : ''
              }`}
              onFocus={() => handleFieldFocus?.(`custom_fields-customInput-type-${index}`)}
            >
              <StyledDropdown
                dataIndex={index}
                values={Object.values(INPUT_TYPE_MAPPING)}
                selectedValue={
                  customInput.type === 'Select an Input Type'
                    ? customInput.type
                    : INPUT_TYPE_MAPPING[customInput.type as keyof typeof INPUT_TYPE_MAPPING]
                }
                setSelectedValue={handleTypeChange}
                width={'w-full'}
              />
            </div>
          </div>

          {/* Options Field (for dropdown types) */}
          {(customInput.type === 'SINGLE_SELECTION_DROPDOWN' ||
            customInput.type === 'MULTI_SELECTION_DROPDOWN') && (
            <div className="space-y-1">
              <Label className="text-base font-medium text-ink-secondary">Options</Label>
              <div onFocus={() => handleFieldFocus?.(`custom_fields-customInput-options-${index}`)}>
                <MultiSelectDropdownNew
                  height={28}
                  options={[]}
                  items={customInput.options || []}
                  setItems={(options: string[]) => handleOptionsChange(options, index)}
                  error={isFieldInvalid?.(`custom_fields-customInput-options-${index}`)}
                />
              </div>
            </div>
          )}

          {/* Required Toggle */}
          <div className="flex items-center gap-1">
            <Switch
              checked={customInput.required}
              onCheckedChange={(checked) => handleRequiredChange(checked, index)}
            />
            <Label className="text-base font-medium text-ink-secondary">Required</Label>
          </div>
        </div>
      )}
    </Draggable>
  );
}

export default CustomInputsTabContent;
